/**
 * Where a signed-in staff user lands, and which tenant they land in (ADR 0126).
 *
 * This used to live twice — once in `app/page.tsx` (the `/` landing) and once in
 * `app/(dashboard)/layout.tsx` — and the two drifted: the landing routed purely on
 * `org_roles.length` while the layout had a super_admin escape hatch. A super_admin
 * who happened to belong to several orgs got bounced to `/select-org` by the landing
 * that the layout would never have bounced. Both now call `resolveLanding` so there
 * is one answer to the question.
 *
 * The rule that matters: **a super_admin may enter every org, not just the ones they
 * are a member of.** Their org list comes from `/admin/orgs`, never from the JWT's
 * `org_roles`, and membership is not consulted anywhere on their path. (The JWT's
 * `org_roles` for a super_admin is incidental — in dev it even contains an org with
 * no `tenant_configs` document at all.)
 */

import type { HostLock } from "./host-brand";

export interface TenantContext {
  /** `localStorage.active_tenant_id` — the tenant last chosen, if any. */
  storedTenantId: string | null;
  /** Org ids from the JWT's `org_roles` (real membership). Ignored for super_admin. */
  memberOrgIds: string[];
  /** `tms_system_role === "super_admin"` — from `me/profile`, NOT the JWT. */
  isSuperAdmin: boolean;
  /**
   * Whether `storedTenantId` still names a usable org, for super_admin only.
   * `null` means "not checked yet" and must keep them where they are rather than
   * guessing — see `resolveLanding`. Verify with `GET /admin/orgs/:id` per org, never
   * against a page of `/admin/orgs`: that endpoint paginates, so a stored org sitting
   * on page 2 would look deleted.
   */
  storedOrgStatus: StoredOrgStatus | null;
  /**
   * ADR 0418 D8 — on an org's own domain the user may only work in that org. Omitted / `none` =
   * no lock (plain localhost, unknown host): everything below behaves exactly as before.
   * super_admin is exempt (they may enter every org from any domain).
   */
  hostLock?: HostLock;
}

export type StoredOrgStatus = "ok" | "missing" | "deleted";

export type LandingDecision =
  // `/dashboard` can carry `clearStoredTenant` too: dropping a stale tenant and then
  // finding exactly one org left resolves to the dashboard, not the picker.
  | { route: "/dashboard"; adoptTenantId: string | null; clearStoredTenant?: boolean }
  | { route: "/select-org"; clearStoredTenant?: boolean }
  | { route: "/create-org"; clearStoredTenant?: boolean }
  /** ADR 0418 frame 3 — this domain's org is not one of the user's. */
  | { route: "/wrong-host"; clearStoredTenant?: boolean }
  /** Not enough information yet — render a loader, navigate nowhere. */
  | { route: null; clearStoredTenant?: undefined };

export function resolveLanding(ctx: TenantContext): LandingDecision {
  const { storedTenantId, memberOrgIds, isSuperAdmin, storedOrgStatus } = ctx;
  const lock = ctx.hostLock ?? { status: "none" };

  if (!isSuperAdmin && lock.status === "pending") return { route: null };
  if (!isSuperAdmin && lock.status === "locked") {
    // The domain decides the org: never "self-heal" into another one the user also belongs to.
    if (!lock.tenantId) return { route: "/wrong-host", clearStoredTenant: true };
    return { route: "/dashboard", adoptTenantId: storedTenantId === lock.tenantId ? null : lock.tenantId };
  }

  if (isSuperAdmin) {
    if (storedTenantId) {
      // Optimistic on purpose: honour the stored tenant before the org check comes
      // back. Waiting would blank the dashboard on every load, and a stale tenant is
      // rare — so we let them in and correct course below once we know.
      if (storedOrgStatus === null || storedOrgStatus === "ok") {
        return { route: "/dashboard", adoptTenantId: null };
      }
      // Deleted or nonexistent: not a workspace anyone should be operating in.
      return { route: "/select-org", clearStoredTenant: true };
    }
    // No stored tenant. Never auto-adopt: `/admin/orgs` is ordered by `createdAt`
    // desc, so "the first org" means "whichever was created most recently" — a
    // meaningless default for someone who administers all of them, and one that
    // invites edits landing in the wrong org. Let them choose.
    return { route: "/select-org" };
  }

  if (storedTenantId) {
    // An empty `memberOrgIds` is not evidence of anything (a JWT whose claims lag
    // behind reality looks identical), so only drop the stored tenant when we have a
    // membership list to contradict it.
    if (memberOrgIds.length === 0 || memberOrgIds.includes(storedTenantId)) {
      return { route: "/dashboard", adoptTenantId: null };
    }
    return { ...decideFromMembership(memberOrgIds), clearStoredTenant: true };
  }

  return decideFromMembership(memberOrgIds);
}

function decideFromMembership(
  memberOrgIds: string[],
): { route: "/dashboard"; adoptTenantId: string } | { route: "/select-org" } | { route: "/create-org" } {
  if (memberOrgIds.length === 0) return { route: "/create-org" };
  if (memberOrgIds.length === 1) {
    return { route: "/dashboard", adoptTenantId: memberOrgIds[0] };
  }
  return { route: "/select-org" };
}

/**
 * Classify a `GET /admin/orgs/:id` outcome into `storedOrgStatus`.
 *
 * A transport failure is NOT `"missing"`. Conflating them would let a dropped
 * request evict a super_admin from an org that is perfectly fine, so anything other
 * than a definitive 404 or a definitive document leaves the status unknown.
 */
export function classifyStoredOrg(
  outcome:
    | { kind: "found"; deletedAt: string | null | undefined }
    | { kind: "not-found" }
    | { kind: "error" },
): StoredOrgStatus | null {
  if (outcome.kind === "not-found") return "missing";
  if (outcome.kind === "found") return outcome.deletedAt ? "deleted" : "ok";
  return null;
}
